/**
 * ServingBadge — what the gateway's newest probe says about a deployment.
 *
 * Four states rather than two, because "serving" was hiding a real one:
 * a Baseten deployment that has scaled to zero is UP (it wakes on the next
 * request) but has no warm replica. Calling that "Serving" promises a
 * latency it won't deliver; calling it "Down" is wrong and would page
 * somebody. It gets its own word.
 *
 * `idle` only ever appears on Baseten-backed models. W&B hosts the
 * inference itself, so it has no scale-to-zero state — a W&B model is only
 * ever live, down or unknown, and that is complete rather than missing one.
 *
 * `unknown` means nobody has probed yet — the gateway only probes
 * upstreams with a healthCheck config, so hyperscaler upstreams
 * (anthropic/*) never get one. That is absence of data, not a state, so
 * it renders as the table's null placeholder rather than a chip that
 * reads like a warning.
 */

import type { DashboardModelRow } from '@/api/generated/model'
import { Badge } from '@/components/ui/badge'

type Status = DashboardModelRow['status']

const STATUS: Record<
  Exclude<Status, 'unknown'>,
  { label: string; variant: 'success' | 'outline' | 'destructive'; title: string }
> = {
  live: {
    label: 'Live',
    variant: 'success',
    title: 'Probed healthy. Replicas are warm',
  },
  idle: {
    label: 'Idle',
    variant: 'outline',
    title: 'Scaled to zero. Wakes on the next request',
  },
  down: {
    label: 'Down',
    variant: 'destructive',
    title: 'The last probe failed',
  },
}

export function ServingBadge({ status }: { status: Status }): React.JSX.Element {
  if (status === 'unknown') {
    return (
      <span title="Not probed" className="text-muted-foreground">
        <span className="sr-only">Not probed</span>
        <span aria-hidden="true">—</span>
      </span>
    )
  }
  const { label, variant, title } = STATUS[status]
  return (
    <Badge variant={variant} title={title}>
      {label}
    </Badge>
  )
}
